import { useEffect, useRef, useState } from "react";
import { fetchGeoHint } from "../api/geo";
import { Button } from "../components/Button";
import { COUNTRY_OPTIONS, countryName } from "../domain/countries";
import { useFlow } from "../flow/FlowContext";
import { useT } from "../i18n";

export function ProfileScreen() {
  const { answers, setProfile, next, canContinue } = useFlow();
  const t = useT();
  const geoTried = useRef(false);
  const [geoNote, setGeoNote] = useState<string | null>(null);

  const patch = (partial: {
    firstName?: string;
    age?: string;
    city?: string;
    country?: string;
    gender?: "male" | "female" | null;
  }) => {
    setProfile(
      partial.firstName ?? answers.firstName,
      partial.age ?? answers.age,
      partial.city ?? answers.city,
      partial.gender !== undefined ? partial.gender : answers.gender,
      partial.country !== undefined ? partial.country : answers.country
    );
  };

  useEffect(() => {
    if (geoTried.current) return;
    if (answers.country.trim()) return;
    geoTried.current = true;
    const ac = new AbortController();
    void (async () => {
      const hint = await fetchGeoHint(ac.signal);
      if (!hint.country) {
        setGeoNote(t("profile.geoUnavailable"));
        return;
      }
      setProfile(
        answers.firstName,
        answers.age,
        answers.city,
        answers.gender,
        hint.country
      );
      setGeoNote(
        t("profile.geoDetected", {
          country: hint.countryName || countryName(hint.country) || hint.country,
        })
      );
    })();
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="prompt prompt--sheet screen-enter">
        <header className="screen-head">
          <div className="screen-head__eyebrow">{t("profile.eyebrow")}</div>
          <h1 className="screen-head__title">{t("profile.title")}</h1>
          <p className="screen-head__text">{t("profile.text")}</p>
        </header>
        <div className="prompt__sex" role="group" aria-label={t("profile.sex")}>
          <button
            type="button"
            className={`choice-card choice-card--sex${answers.gender === "male" ? " is-selected" : ""}`}
            onClick={() => patch({ gender: "male" })}
            aria-pressed={answers.gender === "male"}
          >
            <strong>{t("common.male")}</strong>
          </button>
          <button
            type="button"
            className={`choice-card choice-card--sex${answers.gender === "female" ? " is-selected" : ""}`}
            onClick={() => patch({ gender: "female" })}
            aria-pressed={answers.gender === "female"}
          >
            <strong>{t("common.female")}</strong>
          </button>
        </div>
        <div className="stack">
          <label className="field">
            <span className="sr-only">{t("profile.firstName")}</span>
            <input
              className="field__input"
              type="text"
              autoComplete="given-name"
              placeholder={t("profile.firstNamePlaceholder")}
              value={answers.firstName}
              onChange={(e) => patch({ firstName: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="sr-only">{t("profile.age")}</span>
            <input
              className="field__input"
              type="number"
              min={18}
              max={99}
              placeholder={t("profile.agePlaceholder")}
              value={answers.age}
              onChange={(e) => patch({ age: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="sr-only">{t("profile.country")}</span>
            <select
              className="field__input"
              value={answers.country}
              onChange={(e) => patch({ country: e.target.value })}
              aria-label={t("profile.country")}
            >
              <option value="">{t("profile.countryPlaceholder")}</option>
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
            {geoNote ? <span className="field__meta">{geoNote}</span> : null}
          </label>
          <label className="field">
            <span className="sr-only">{t("profile.city")}</span>
            <input
              className="field__input"
              type="text"
              autoComplete="address-level2"
              placeholder={t("profile.cityPlaceholder")}
              value={answers.city}
              onChange={(e) => patch({ city: e.target.value })}
            />
          </label>
        </div>
      </div>
      <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Button onClick={next} disabled={!canContinue}>
          {t("common.next")}
        </Button>
      </div>
    </>
  );
}
